import ChatBubbleDisclosure from '../ChatBubbleDisclosure';
import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import type { ConstructionSowChatField, ConstructionSowChatSessionView } from '../ConstructionSowChat/types';
import type { ServicesPwsChatSessionView } from '../ServicesPwsChat/types';

type ControllerView = ConstructionSowChatSessionView | ServicesPwsChatSessionView;
export type WorkflowTranscriptTurn = { id: string; role: 'assistant' | 'user'; heading?: string; content?: string; items?: string[]; files?: string[] };
const fileLabels: Record<string, string> = { requirements: 'Requirements package', macc: 'MACC / MATOC', template: 'Template / reference', drawings: 'New Construction Drawings', as_built_drawings: 'As-Built Drawings', ae_guide: 'A/E guide', other: 'Other support document' };
const text = (value: unknown): string => typeof value === 'string' || typeof value === 'boolean' || typeof value === 'number' ? String(value) : '';
const key = (field: ConstructionSowChatField) => field.id || field.key || '';
function required(field: ConstructionSowChatField): boolean {
  if (field.kind === 'proposal') return false;
  if (typeof field.required === 'boolean') return field.required;
  if (typeof field.isRequired === 'boolean') return field.isRequired;
  return typeof field.priority === 'string' ? ['high', 'required'].includes(field.priority.toLowerCase().trim()) : field.priority === 1 || field.priority === 2;
}

/** Context answers live in controller/stage storage, not Home Chat messages.
 * Project only the current workflow's durable read model; never reopen forms. */
export function projectWorkflowControllerTranscript(view: ControllerView | null, services: boolean, existingUserMessages: readonly string[] = [], existingAssistantMessages: readonly string[] = []): WorkflowTranscriptTurn[] {
  if (!view) return [];
  const turns: WorkflowTranscriptTurn[] = [];
  const cursor = text(view.cursor);
  const requirement = text(view.intake?.requirement ?? view.intake?.requirement_text);
  const requirementFilename = text(view.intake?.requirement_document?.filename).trim();
  const files = Array.from(new Set([
    ...(requirementFilename ? [`Requirements package: ${requirementFilename}`] : []),
    ...(view.intake?.documents ?? view.intake?.artifacts ?? []).flatMap(artifact => {
      const filename = text(artifact.filename).trim();
      return filename ? [`${fileLabels[artifact.role] || 'Support document'}: ${filename}`] : [];
    }),
  ]));
  const intakeText = requirement.trim() && !existingUserMessages.some(message => message.trim() === requirement.trim()) ? requirement : undefined;
  if (cursor !== 'intake' && (intakeText || files.length)) {
    turns.push({ id: 'intake', role: 'user', content: intakeText, ...(files.length ? { files } : {}) });
  }
  ([1, 2] as const).forEach(round => {
    const saved = view.question_rounds?.[round === 1 ? 'round_1' : 'round_2'];
    const legacy = Number(view.questions?.round) === round ? view.questions : null;
    const status = text(saved?.status ?? legacy?.status);
    const submitted = ['complete', 'completed', 'submitted', 'closed', 'answered'].includes(status)
      || (round === 1 ? ['round_2', 'ready_to_generate', 'generating', 'handoff_ready'] : ['ready_to_generate', 'generating', 'handoff_ready']).includes(cursor);
    if (!submitted) return;
    const seen = new Set<string>();
    const fields = [...(saved?.fields ?? legacy?.items ?? [])]
      .filter(field => {
        const id = key(field);
        if (!id || seen.has(id) || id === 'csow_project_outcome' || text(field.field) === 'project_outcome' || (services && id === 'spws_project_number')) return false;
        seen.add(id);
        return true;
      })
      .map((field, index) => ({ field, index }))
      .sort((a, b) => Number(required(b.field)) - Number(required(a.field)) || a.index - b.index)
      .map(({ field }) => field);
    if (!fields.length) return;
    const answers = saved?.answers ?? legacy?.answers ?? {};
    turns.push({ id: `round-${round}-questions`, role: 'assistant', heading: round === 1 ? 'Context Questions' : 'Follow-up Questions', items: fields.map(field => text(field.question ?? field.label) || key(field)) });
    turns.push({ id: `round-${round}-answers`, role: 'user', heading: 'Answers', items: fields.map(field => text(answers[key(field)]).trim() || 'Not provided') });
  });
  const assistantMessage = text(view.assistant_message).trim();
  const alreadyShown = [...existingAssistantMessages, ...turns.filter(turn => turn.role === 'assistant').flatMap(turn => [turn.heading || '', turn.content || '', ...(turn.items || []), (turn.items || []).join('\n')])];
  if (assistantMessage && !alreadyShown.some(message => message.trim() === assistantMessage)) {
    turns.push({ id: 'assistant-message', role: 'assistant', content: assistantMessage });
  }
  return turns;
}

export default function WorkflowControllerTranscript({ turns, minimal = false, scopeKey = '' }: { turns: WorkflowTranscriptTurn[]; minimal?: boolean; scopeKey?: string }) {
  const renderTurn = (turn: WorkflowTranscriptTurn) => {
    const round = /^round-([12])-(questions|answers)$/.exec(turn.id);
    const intake = turn.id === 'intake';
    const title = turn.heading ? <Typography variant="overline">{turn.heading}</Typography> : intake ? <Typography variant="overline">Submitted requirement</Typography> : undefined;
    const content = <>
      {!round && !intake && title}
      {turn.content && <Typography sx={{ whiteSpace: 'pre-wrap' }}>{turn.content}</Typography>}
      {turn.files?.map(file => <Typography key={file} variant="caption" sx={{ display: 'block', whiteSpace: 'pre-wrap' }}>{file}</Typography>)}
      {turn.items && <Box component="ol" sx={{ my: 0, pl: 3 }}>{turn.items.map((item, index) => <Box component="li" key={index} sx={{ py: 0.4 }}><Typography sx={{ whiteSpace: 'pre-wrap' }}>{item}</Typography></Box>)}</Box>}
    </>;
    return <Paper key={`${scopeKey}:${turn.id}`} data-role={turn.role} elevation={0} sx={{ alignSelf: turn.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: 'min(100%, 680px)', px: 2, py: 1.5, borderRadius: 2, bgcolor: turn.role === 'user' ? 'primary.main' : 'action.hover', color: turn.role === 'user' ? 'primary.contrastText' : 'text.primary' }}>
      {round || intake ? <ChatBubbleDisclosure alwaysCollapsible title={title} label={intake ? 'submitted workflow intake' : `${round![1] === '2' ? 'follow-up' : 'context'} ${round![2]}`} preview="">{content}</ChatBubbleDisclosure> : content}
    </Paper>;
  };
  return <Box component="section" aria-label="Workflow setup conversation" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
    {!minimal && <Typography variant="caption" color="text.secondary">Workflow setup conversation</Typography>}
    {turns.map(renderTurn)}
  </Box>;
}
